"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../lib/axios"
import type { Kanban, Lead } from "../types/lead"

export const useLeads = () => {
  return useQuery({
    queryKey: ["leads"],
    queryFn: async (): Promise<Kanban[]> => {
      const response = await api.get("/leads/kanban")
      return response.data
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 1000,
    gcTime: 0,
    refetchOnMount: 'always',
  })
}

export const useLead = (leadId: string) => {
  return useQuery({
    queryKey: ["lead", leadId],
    queryFn: async (): Promise<Lead> => {
      const response = await api.get(`/leads/${leadId}`)
      return response.data
    },
    enabled: !!leadId,
    staleTime: 30,
    refetchInterval: 3000
  })
}

export const useUpdateLead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId, data }: { leadId: string; data: Partial<Lead> }) => {
      const response = await api.put(`/leads/${leadId}`, data)
      return response.data
    },
    onSuccess: (_, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] })
      queryClient.invalidateQueries({ queryKey: ["leads"] })
    },
  })
}

export const useNegotiation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId, negotiation }: { leadId: string; negotiation: boolean }) => {
      const response = await api.put(`/leads/negotiation/${leadId}`, { negotiation })
      return response.data
    },
    onSuccess: (_, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] })
      queryClient.invalidateQueries({ queryKey: ["leads"] })
    },
  })
}